import {skipToken} from '@reduxjs/toolkit/query';
import {AnimatePresence, motion, Variants} from 'framer-motion';
import {
  Attributes,
  Button,
  ButtonGroup,
  Card,
  DataStatus,
  Label,
  openDialog,
  Separator,
  showNotification,
} from 'platform/components';
import {Box, Center, Grid, HStack, Image, Show, Space, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {indexBy, isEmpty, isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  EmployeeAuthorizationIntegrationBody,
  usePostMazdaPortalLoginQuery,
} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {parseDate, useQueryState, useOnMount, useToggle} from 'shared/web';

import {EMPTY_PLACEHOLDER} from '../../../constants/placeholders';
import {useBranches} from '../../../hooks/useBranches';
import mazdaLogoImage from '../assets/mazda-logo.png';
import mercedesLogoImage from '../assets/mercedes-benz-logo.png';
import safLogoImage from '../assets/sag-logo.png';
import tecRMIImage from '../assets/tecrmi-logo.png';
import {INTEGRATION_CODES} from '../constants/IntegrationCodes';
import {useListCredentials} from '../hooks/useListCredentials';
import {EditIntegrationForm} from './EditIntegrationForm';

interface IntegrationBoxProps {
  integrations: EmployeeAuthorizationIntegrationBody[];
  integrationCode: INTEGRATION_CODES;
  userId: string | undefined;
  isLoading: boolean;
  isReadonly?: boolean;
}

export function IntegrationBox(props: IntegrationBoxProps) {
  const [isExpanded, toggleExpanded] = useToggle(false);
  const [error, , clearError] = useQueryState('is_error');
  const isMazdaPortal = props.integrationCode === INTEGRATION_CODES.MAZDA_PORTAL;

  const formatDateTime = useDateTimeFormatter();

  const buttonTitle = isExpanded
    ? i18n.t('page.rentalOrder.labels.showLess')
    : `${i18n.t('page.rentalOrder.labels.showMore')} (${props.integrations.length})`;

  const buttonIcon = isExpanded ? 'navigation/expand_less' : 'navigation/expand_more';

  const {data: branches, isLoading: isLoadingBranches} = useBranches();
  const branchesById = indexBy((branch) => branch.id, branches?.branchListItems ?? []);

  const {
    credentials,
    isLoading: isLoadingExternalCredentials,
    refetch: refetchExternalCredentials,
  } = useListCredentials({userId: props.userId ?? '', integrations: props.integrations});

  const mazdaAuthorizationProfileId = props.integrations[0]?.authorizationProfileId;

  const {data: mazdaPortalData} = usePostMazdaPortalLoginQuery(
    isMazdaPortal && !props.isReadonly && isNotNil(mazdaAuthorizationProfileId)
      ? {
          authorizationProfileId: mazdaAuthorizationProfileId,
          destination: window.location.href,
        }
      : skipToken
  );
  const mazdaPortalRedirectUrl = mazdaPortalData?.redirectUrl;
  const additionalHeaderButton =
    isMazdaPortal && isNotNil(mazdaPortalRedirectUrl)
      ? {
          variant: 'outlined' as const,
          size: 'small' as const,
          leftIcon: 'action/open_in_new' as const,
          title: i18n.t('page.employeeDetail.actions.loginToMazdaPortal'),
          onClick: () => {
            // eslint-disable-next-line react-hooks/immutability
            window.location.href = mazdaPortalRedirectUrl;
          },
        }
      : undefined;

  useOnMount(() => {
    if (error && isMazdaPortal) {
      showNotification.error(i18n.t('page.employeeDetail.labels.mazdaPortalError'));
      clearError();
    }
  });

  const handleEditIntegration = (
    id: string,
    title: string,
    credentialValues: Record<string, unknown>
  ) => {
    if (isNil(props.userId)) {
      showNotification.error();
      throw new Error('User ID is required');
    }

    if (props.integrationCode === INTEGRATION_CODES.MAZDA_PORTAL) {
      return;
    }

    const integrationCode = props.integrationCode;

    openDialog(
      <EditIntegrationForm
        authorizationProfileId={id}
        integrationCode={integrationCode}
        userId={props.userId}
        defaultValues={credentialValues}
        onAfterSubmit={refetchExternalCredentials}
      />,
      {title}
    );
  };

  const isLoading = isLoadingBranches || isLoadingExternalCredentials;
  const logo = match(props.integrationCode)
    .with(INTEGRATION_CODES.MERCEDES_XENTRY, () => (
      <Image fit="contain" src={mercedesLogoImage} width={30} height={30} />
    ))
    .with(INTEGRATION_CODES.MERCEDES_CLARA, () => (
      <Image fit="contain" src={mercedesLogoImage} width={30} height={30} />
    ))
    .with(INTEGRATION_CODES.MERCEDES_DAIMLER_XENTRY, () => (
      <Image fit="contain" src={mercedesLogoImage} width={30} height={30} />
    ))
    .with(INTEGRATION_CODES.OTHER_SAG, () => (
      <Image fit="contain" src={safLogoImage} width={30} height={30} />
    ))
    .with(INTEGRATION_CODES.OTHER_TECRMI, () => (
      <Image fit="contain" src={tecRMIImage} width={30} height={30} />
    ))
    .with(INTEGRATION_CODES.MAZDA_PORTAL, () => (
      <Image fit="contain" src={mazdaLogoImage} width={20} height={20} />
    ))
    .exhaustive();

  return (
    <Box boxShadow="elevation_1" maxWidth={275}>
      <HStack>
        <Box width={40} minWidth={40} backgroundColor="palettes.neutral.20.100">
          <Center height={35}>{logo}</Center>
        </Box>

        <Box padding={4} backgroundColor="palettes.white.10.100" flexGrow={1}>
          <VStack>
            <HStack align="center" justify="space-between">
              <VStack>
                <Label>{i18n.t('general.labels.name')}</Label>
                <Text alternative>
                  {i18n.t(`page.employeeDetail.labels.${props.integrationCode}`)}
                </Text>
              </VStack>

              <Show when={isNotNil(additionalHeaderButton)}>
                <Button {...additionalHeaderButton!} />
              </Show>
            </HStack>

            <Separator />

            <DataStatus isLoading={props.isLoading} isEmpty={isEmpty(props.integrations)}>
              <ButtonGroup>
                <Button
                  variant="link"
                  size="small"
                  leftIcon={buttonIcon}
                  title={buttonTitle}
                  onClick={toggleExpanded}
                />
              </ButtonGroup>

              <Space vertical={4} />

              <AnimatePresence initial={false}>
                {isExpanded && (
                  <motion.div
                    initial="collapsed"
                    animate="expanded"
                    exit="collapsed"
                    variants={variants}
                    transition={{duration: 0.3, ease: [0.25, 0.1, 0.25, 1]}}
                    style={{width: '100%'}}
                  >
                    <VStack spacing={2} width="100%">
                      {props.integrations.map((integration) => {
                        const integrationBranch = branchesById[integration.branchId];
                        const credential = credentials?.credentials?.find(
                          (credential) =>
                            credential.authorizationProfileId ===
                              integration.authorizationProfileId &&
                            credential.integratedServiceCode === props.integrationCode
                        );

                        const credentialValues = credential?.values ?? [];

                        const expirationValue = credentialValues.find(
                          (v) => v.name === 'expirationDateTime'
                        )?.value;
                        const isExpired =
                          isNotNil(expirationValue) && new Date() > parseDate(expirationValue);
                        const isActive = Boolean(credential?.enabled) && !isExpired;

                        const title = isNil(credential)
                          ? i18n.t('page.employeeDetail.labels.setup')
                          : i18n.t('general.labels.edit');

                        return (
                          <Card
                            key={integration.authorizationProfileId}
                            variant="inlineGrey"
                            title={integration.authorizationProfileName}
                            flags={
                              isLoading
                                ? []
                                : [
                                    {
                                      label: isActive
                                        ? i18n.t('general.labels.active')
                                        : i18n.t('entity.employee.labels.flagInactive'),
                                      colorScheme: isActive ? 'green' : 'red',
                                    },
                                  ]
                            }
                            actions={
                              isMazdaPortal
                                ? []
                                : [
                                    {
                                      type: 'button',
                                      isDisabled: isLoading,
                                      variant: 'outlined',
                                      title,
                                      leftIcon: 'image/edit',
                                      onClick: () =>
                                        handleEditIntegration(
                                          integration.authorizationProfileId,
                                          title,
                                          credentialValues.reduce(
                                            (acc, value) => {
                                              acc[value.name] = value.value;
                                              return acc;
                                            },
                                            {} as Record<string, unknown>
                                          )
                                        ),
                                    },
                                  ]
                            }
                          >
                            <DataStatus isLoading={isLoading} minHeight={30}>
                              <VStack spacing={4}>
                                <Grid columns={4} align="center">
                                  <VStack>
                                    <Label>{i18n.t('entity.branch.labels.branch')}</Label>
                                    <Text alternative>
                                      {integrationBranch?.marketingName || EMPTY_PLACEHOLDER}
                                    </Text>
                                  </VStack>

                                  <VStack>
                                    <Label>{i18n.t('general.labels.createdAt')}</Label>
                                    <Text alternative>
                                      {credential?.createdAt
                                        ? formatDateTime(
                                            'dateShort',
                                            parseDate(credential.createdAt)
                                          )
                                        : EMPTY_PLACEHOLDER}
                                    </Text>
                                  </VStack>

                                  <VStack>
                                    <Label>{i18n.t('general.labels.updatedAt')}</Label>
                                    <Text alternative>
                                      {credential?.updatedAt
                                        ? formatDateTime(
                                            'dateShort',
                                            parseDate(credential.updatedAt)
                                          )
                                        : EMPTY_PLACEHOLDER}
                                    </Text>
                                  </VStack>
                                </Grid>

                                <DataStatus
                                  isEmpty={isNilOrEmpty(credential?.values)}
                                  spacing={4}
                                  emptyMessage={i18n.t(
                                    'page.employeeDetail.labels.integrationNotSetup'
                                  )}
                                >
                                  <Separator spacing={0} />
                                  <Attributes
                                    size="third"
                                    rows={credential?.values?.map((item) => ({
                                      label: item.name,
                                      value: item.value,
                                    }))}
                                  />
                                </DataStatus>
                              </VStack>
                            </DataStatus>
                          </Card>
                        );
                      })}
                    </VStack>
                  </motion.div>
                )}
              </AnimatePresence>
            </DataStatus>
          </VStack>
        </Box>
      </HStack>
    </Box>
  );
}

const variants: Variants = {
  collapsed: {
    height: 0,
    opacity: 0,
    overflow: 'hidden',
  },
  expanded: {
    height: 'auto',
    opacity: 1,
    overflow: 'visible',
  },
};
